import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import SearchBar from '../components/SearchBar';
import StatusBadge from '../components/StatusBadge';
import ClinicHubDialog from '../components/ClinicHubDialog';
import hubService from '../services/hubService';
import userService from '../services/userService';
import { repClinicNames, repName } from '../utils/repTree';
import { money } from '../utils/money';
import { useLocale, useT } from '../i18n/LanguageContext';

/**
 * MY HUB (9/4/26) - a sales rep's clinics, one card each, and what each clinic's own logins may do.
 *
 * Kevin's feedback after the demo: not every clinic should submit its own orders (some are
 * read-only - he places their orders), one clinic only ever orders a single product so that is all
 * its login should see, and he wants each clinic's credit picture in front of him. The card shows
 * the three settings at a glance; "Manage" opens ClinicHubDialog to change them. The server scopes
 * the list (a rep gets their assigned clinics, an admin every clinic) and says whether the caller
 * may change anything (`canManage`).
 *
 * A CLINIC LOGIN gets the same page for its own clinic only, read-only: the card without Manage,
 * plus a plain-language panel spelling out what the settings mean for them. Nothing here is a
 * gate - the server refuses an order from a switched-off clinic and filters the product list.
 *
 * The TEAM tab is the old My Team page moved over verbatim: the other reps this rep has been
 * granted sight of. It loads only when opened, because most reps have nobody there.
 */

const unwrapTeam = (res) => {
  const data = res?.data?.data?.data || res?.data?.data || [];
  return Array.isArray(data) ? data : [];
};

const PILL = 'inline-flex items-center h-[22px] px-2 rounded-full text-[11px] font-semibold whitespace-nowrap';
const PILL_ON = `${PILL} bg-[#defced] text-[#007a55]`;
const PILL_OFF = `${PILL} bg-[#ffebec] text-[#f23e41]`;
const PILL_ALL = `${PILL} bg-[#e6f1ff] text-[#0069c4]`;
const PILL_SOME = `${PILL} bg-[#f3e8ff] text-[#7e22ce]`;

function Spinner() {
  return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
    </div>
  );
}

function ErrorBanner({ message }) {
  const t = useT();
  return (
    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" role="alert">
      {t(message)}
    </div>
  );
}

/** One number with its label - the summary strip across the top. */
function StatPill({ label, value, tone }) {
  const t = useT();
  return (
    <div className="bg-[#f7f8fb] rounded-[10px] px-4 py-3 min-w-[120px]">
      <p className="text-[11px] font-medium text-[#64748b]">{t(label)}</p>
      <p className={`text-xl font-semibold leading-tight ${tone || 'text-[#0f172a]'}`}>{value}</p>
    </div>
  );
}

function CardRow({ label, children }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[12px] font-medium text-[#64748b]">{t(label)}</span>
      <span className="text-[12px] font-semibold text-[#0f172a] text-right min-w-0">{children}</span>
    </div>
  );
}

const productCount = (clinic) => (Array.isArray(clinic.allowedProducts) ? clinic.allowedProducts.length : null);
const overdueCount = (clinic) => clinic.ledger?.overdueCount || 0;
const outstandingOf = (clinic) => Number(clinic.ledger?.outstanding) || 0;
const hasLimit = (clinic) => clinic.creditLimit !== null && clinic.creditLimit !== undefined;

function CreditBar({ clinic }) {
  if (!hasLimit(clinic) || !(clinic.creditLimit > 0)) return null;
  const outstanding = outstandingOf(clinic);
  const over = outstanding > clinic.creditLimit;
  const pct = Math.min(100, Math.round((outstanding / clinic.creditLimit) * 100));
  return (
    <div className="h-1.5 w-full rounded-full bg-[#e2e8f0] overflow-hidden mt-1" aria-hidden="true">
      <div
        className={`h-full rounded-full ${over ? 'bg-[#f23e41]' : 'bg-[#0089ff]'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ClinicCard({ clinic, products, canManage, onManage }) {
  const t = useT();
  const locale = useLocale();
  const logins = (clinic.logins || []).length;
  const limited = productCount(clinic);
  const overdue = overdueCount(clinic);
  const changedBy = clinic.hubUpdatedBy ? repName(clinic.hubUpdatedBy) : '';

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-hidden flex flex-col">
      <div className="p-5 flex-1">
        <p className="text-[14px] font-semibold text-[#0f172a] truncate" title={clinic.name}>
          {clinic.name}
        </p>
        <p className="text-xs font-medium text-[#64748b] truncate mb-3" title={clinic.companyName || ''}>
          {[clinic.companyName, t(logins === 1 ? '1 login' : '{n} logins', { n: logins })]
            .filter(Boolean)
            .join(' · ')}
        </p>

        <div className="h-px bg-[#e2e8f0] mb-2" />

        <CardRow label="Ordering">
          {clinic.orderingEnabled ? (
            <span className={PILL_ON}>{t('On')}</span>
          ) : (
            <span className={PILL_OFF}>{t('Off · read-only')}</span>
          )}
        </CardRow>

        <CardRow label="Products">
          {limited === null ? (
            <span className={PILL_ALL}>{t('All')}</span>
          ) : (
            <span className={PILL_SOME}>
              {products.length > 0
                ? t('{n} of {total}', { n: limited, total: products.length })
                : t(limited === 1 ? '{n} product' : '{n} products', { n: limited })}
            </span>
          )}
        </CardRow>

        <CardRow label="Credit">
          {!hasLimit(clinic)
            ? t('No limit set')
            : clinic.ledger
              ? t('{outstanding} of {limit}', {
                  outstanding: money(outstandingOf(clinic)),
                  limit: money(clinic.creditLimit),
                })
              : money(clinic.creditLimit)}
        </CardRow>
        <CreditBar clinic={clinic} />

        {overdue > 0 && (
          <p className="mt-2 text-[12px] font-semibold text-[#c25e16] flex items-center gap-1">
            <span className="material-symbols-outlined text-[16px]" aria-hidden="true">warning</span>
            {t(overdue === 1 ? '1 past due' : '{n} past due', { n: overdue })}
          </p>
        )}
      </div>

      <div className="px-5 py-2.5 bg-[rgba(226,232,240,0.2)] border-t border-[#e2e8f0] flex items-center justify-between gap-2">
        <span className="text-[10px] font-medium text-[#64748b] truncate min-w-0">
          {clinic.hubUpdatedAt && changedBy
            ? t('Last changed by {name}, {date}', {
                name: changedBy,
                date: new Date(clinic.hubUpdatedAt).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }),
              })
            : ''}
        </span>
        {canManage && (
          <button
            type="button"
            onClick={() => onManage(clinic)}
            className="h-[30px] px-3 shrink-0 rounded-[8px] border border-[#d6dce8] bg-white text-[12px] font-semibold text-[#0f172a] hover:bg-gray-50 transition-colors"
          >
            {t('Manage')}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The read-only explanation for a clinic login: the same facts as the card, but as sentences,
 * because "Off · read-only" on a pill does not tell a front-desk user what to do instead.
 */
function ClinicDetails({ clinic }) {
  const t = useT();
  const limit = hasLimit(clinic) ? Number(clinic.creditLimit) : null;
  const outstanding = outstandingOf(clinic);
  const overdue = overdueCount(clinic);

  return (
    <section className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-5">
      <h2 className="text-[15px] font-semibold text-[#0f172a] mb-3">{t('What your clinic can do')}</h2>
      <dl className="space-y-3">
        <div>
          <dt className="text-[11px] font-medium text-[#64748b]">{t('Ordering')}</dt>
          <dd className="text-[13px] text-[#0f172a] mt-0.5">
            {clinic.orderingEnabled
              ? t('Your clinic can submit orders.')
              : t('Ordering is turned off for your clinic. Your sales representative places orders for you.')}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-medium text-[#64748b]">{t('Products')}</dt>
          <dd className="text-[13px] text-[#0f172a] mt-0.5">
            {Array.isArray(clinic.allowedProducts) && clinic.allowedProducts.length === 0 ? (
              t('No products in the catalog.')
            ) : Array.isArray(clinic.allowedProducts) ? (
              <ul className="list-disc pl-5">
                {clinic.allowedProducts.map((p) => (
                  <li key={p._id}>{p.name}</li>
                ))}
              </ul>
            ) : (
              t('All products')
            )}
          </dd>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <dt className="text-[11px] font-medium text-[#64748b]">{t('Credit limit')}</dt>
            <dd className="text-[13px] font-semibold text-[#0f172a] mt-0.5">
              {limit === null ? t('No limit set') : money(limit)}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] font-medium text-[#64748b]">{t('Outstanding')}</dt>
            <dd className="text-[13px] font-semibold text-[#0f172a] mt-0.5">{money(outstanding)}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-medium text-[#64748b]">{t('Available')}</dt>
            <dd className={`text-[13px] font-semibold mt-0.5 ${limit !== null && outstanding > limit ? 'text-[#f23e41]' : 'text-[#0f172a]'}`}>
              {limit === null ? '—' : money(Math.max(0, limit - outstanding))}
            </dd>
          </div>
        </div>
        {overdue > 0 && (
          <div>
            <dt className="sr-only">{t('Overdue')}</dt>
            <dd className="text-[12px] font-semibold text-[#c25e16]">
              {t(overdue === 1 ? '1 past due' : '{n} past due', { n: overdue })}
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

// ------------------------------------------------------------------ Team (the old My Team page)

function TeamRow({ rep }) {
  const t = useT();
  const clinics = repClinicNames(rep);

  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#f1f5f9] last:border-b-0">
      <span className="material-symbols-outlined text-[18px] mt-0.5 text-[#94a3b8]" aria-hidden="true">
        person
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-[#0f172a] truncate">{repName(rep)}</p>
        <p className="text-[11px] text-[#64748b] truncate">{rep.email}</p>
        <p className="text-[12px] text-[#0f172a] mt-1">
          {clinics.length > 0 ? clinics.join(', ') : t('No accounts assigned yet')}
        </p>
      </div>

      <div className="flex items-center gap-5 shrink-0">
        <div className="text-right">
          <p className="text-[10px] font-medium text-[#64748b]">{t('Orders')}</p>
          <p className="text-sm font-semibold text-[#0f172a]">{rep.orderCount || 0}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-medium text-[#64748b]">{t('IVRs')}</p>
          <p className="text-sm font-semibold text-[#0f172a]">{rep.ivrCount || 0}</p>
        </div>
        <StatusBadge status={rep.isActive !== false ? 'active' : 'inactive'} size="small" />
      </div>
    </div>
  );
}

/**
 * The roster only. The records half already happened somewhere else: the grant widens this rep's
 * Orders and IVR lists to include those reps' submissions. So the tab's job is to answer "whose
 * work can I see", and to say plainly that it is already mixed into the other lists. The accounts
 * beside each name are THAT rep's accounts, not accounts the caller can see: a grant covers
 * submissions, not practices. A rep with no grants gets the empty state, not an error.
 */
function TeamTab({ team, loading, error }) {
  const t = useT();
  const rows = team;
  const accounts = new Set(team.flatMap((rep) => repClinicNames(rep))).size;

  // Four sentences rather than one with pluralisation glued in: each is a whole key, so the Spanish
  // can order the words its own way.
  const teamSubtitle = () => {
    if (rows.length === 0) return t('Other reps whose work you can see.');
    const vars = { reps: rows.length, accounts };
    if (rows.length === 1) {
      return t(
        accounts === 1
          ? 'You can see the work of {reps} other rep, across {accounts} of their account. Their orders and IVRs are already included in your own lists.'
          : 'You can see the work of {reps} other rep, across {accounts} of their accounts. Their orders and IVRs are already included in your own lists.',
        vars,
      );
    }
    return t(
      accounts === 1
        ? 'You can see the work of {reps} other reps, across {accounts} of their account. Their orders and IVRs are already included in your own lists.'
        : 'You can see the work of {reps} other reps, across {accounts} of their accounts. Their orders and IVRs are already included in your own lists.',
      vars,
    );
  };

  if (loading) return <Spinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} />}
      <p className="text-[13px] text-[#64748b] mb-3">{teamSubtitle()}</p>
      {rows.length === 0 ? (
        <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-8 text-center">
          <p className="text-sm text-[#0f172a] font-medium">{t('You have not been given access to any other reps')}</p>
          <p className="text-[13px] text-[#64748b] mt-1">
            {t('An administrator grants this per person on the Sales Representatives page. Until then you see your own accounts and your own work only.')}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-5">
          {rows.map((rep) => (
            <TeamRow key={rep._id} rep={rep} />
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ the page

const TABS = [
  { key: 'clinics', label: 'Clinics' },
  { key: 'team', label: 'Team' },
];

export default function MyHub() {
  const t = useT();
  const [clinics, setClinics] = useState([]);
  const [products, setProducts] = useState([]);
  const [canManage, setCanManage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('clinics');
  const [manageRow, setManageRow] = useState(null);

  const [team, setTeam] = useState([]);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');

  // English fallbacks go into state and are translated at render ({t(error)}), so `t` is never a
  // dependency of a fetch.
  useEffect(() => {
    let cancelled = false;
    hubService
      .list()
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data || {};
        setClinics(Array.isArray(payload.data) ? payload.data : []);
        setProducts(Array.isArray(payload.products) ? payload.products : []);
        setCanManage(payload.canManage !== false);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your clinics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The roster loads the first time the Team tab opens, and only then.
  useEffect(() => {
    if (tab !== 'team' || teamLoaded) return undefined;
    let cancelled = false;
    setTeamLoading(true);
    userService
      .getMyTeam()
      .then((res) => {
        if (!cancelled) setTeam(unwrapTeam(res));
      })
      .catch(() => {
        if (!cancelled) setTeamError('Could not load your team');
      })
      .finally(() => {
        if (cancelled) return;
        setTeamLoading(false);
        setTeamLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, teamLoaded]);

  const handleSaved = useCallback((row) => {
    if (!row?._id) return;
    const merge = (c) => (c._id === row._id ? { ...c, ...row } : c);
    setClinics((prev) => prev.map(merge));
    setManageRow((prev) => (prev && prev._id === row._id ? { ...prev, ...row } : prev));
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return clinics;
    return clinics.filter((c) =>
      [c.name, c.companyName].filter(Boolean).join(' ').toLowerCase().includes(needle),
    );
  }, [clinics, search]);

  const stats = useMemo(
    () => ({
      clinics: clinics.length,
      orderingOn: clinics.filter((c) => c.orderingEnabled).length,
      limited: clinics.filter((c) => productCount(c) !== null).length,
      pastDue: clinics.filter((c) => overdueCount(c) > 0).length,
    }),
    [clinics],
  );

  const subtitle = canManage
    ? 'Your clinics, and what each one can do.'
    : 'What your clinic can do, as set by your sales representative.';

  const clinicGrid = (rows) => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-5">
      {rows.map((clinic) => (
        <ClinicCard
          key={clinic._id}
          clinic={clinic}
          products={products}
          canManage={canManage}
          onManage={setManageRow}
        />
      ))}
    </div>
  );

  // A clinic login: its one clinic, read-only, spelled out. No team - a clinic has none.
  if (!loading && !canManage) {
    return (
      <div>
        <PageHeader title="My Hub" subtitle={subtitle} />
        {error && <ErrorBanner message={error} />}
        {clinics.length === 0 ? (
          !error && (
            <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-8 text-center">
              <p className="text-sm text-[#0f172a] font-medium">{t('No clinic is linked to your login yet.')}</p>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,320px)_1fr] gap-5 items-start">
            {clinicGrid(clinics)}
            <div className="space-y-5">
              {clinics.map((clinic) => (
                <ClinicDetails key={clinic._id} clinic={clinic} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="My Hub" subtitle={subtitle}>
        {tab === 'clinics' && !loading && (
          <SearchBar
            placeholder="Search clinics..."
            className="w-[280px] max-w-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </PageHeader>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div
              className="inline-flex flex-wrap gap-1 bg-white border border-[#e2e8f0] rounded-[11px] p-1"
              role="tablist"
              aria-label={t('Hub views')}
            >
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.key}
                  onClick={() => setTab(item.key)}
                  className={`h-[34px] px-3.5 rounded-[8px] text-[13px] font-semibold transition-colors ${
                    tab === item.key ? 'bg-[#0f172a] text-white' : 'text-[#64748b] hover:text-[#0f172a]'
                  }`}
                >
                  {t(item.label)}
                </button>
              ))}
            </div>

            {tab === 'clinics' && (
              <div className="flex flex-wrap gap-2">
                <StatPill label="Clinics" value={stats.clinics} />
                <StatPill label="Ordering on" value={stats.orderingOn} tone="text-[#007a55]" />
                <StatPill label="Limited products" value={stats.limited} tone="text-[#7e22ce]" />
                <StatPill label="Past due" value={stats.pastDue} tone={stats.pastDue > 0 ? 'text-[#c25e16]' : ''} />
              </div>
            )}
          </div>

          {tab === 'team' ? (
            <TeamTab team={team} loading={teamLoading || !teamLoaded} error={teamError} />
          ) : clinics.length === 0 ? (
            !error && (
              <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-8 text-center">
                <p className="text-sm text-[#0f172a] font-medium">{t('No clinics are assigned to you yet.')}</p>
                <p className="text-[13px] text-[#64748b] mt-1">
                  {t('An administrator assigns clinics on the Sales Representatives page.')}
                </p>
              </div>
            )
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#64748b]">{t('No clinics match your search.')}</div>
          ) : (
            clinicGrid(filtered)
          )}
        </>
      )}

      <ClinicHubDialog
        open={!!manageRow}
        clinic={manageRow}
        products={products}
        canManage={canManage}
        onClose={() => setManageRow(null)}
        onSaved={handleSaved}
      />
    </div>
  );
}
